/**
 * Btn - primary interactive element using design token system.
 * Variants: primary | secondary | ghost | danger
 * Sizes: sm | md | lg
 */
import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';

const cls = (...parts: (string | false | undefined | null)[]) =>
  parts.filter(Boolean).join(' ');

export type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type BtnSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<BtnVariant, string> = {
  primary:
    'app-primary-button',
  secondary:
    'border border-border bg-surface text-ink shadow-sm backdrop-blur hover:bg-surface-raised disabled:text-ink-muted',
  ghost:
    'border border-border bg-transparent text-ink hover:bg-surface-raised disabled:text-ink-muted',
  danger:
    'bg-neg text-white shadow-[0_14px_28px_-18px_rgba(207,59,46,0.48)] hover:bg-red-700 active:bg-red-800 disabled:bg-ink-muted/30 disabled:text-ink-muted',
};

const SIZES: Record<BtnSize, string> = {
  sm: 'min-h-[46px] rounded-[15px] px-4 py-2.5 text-small gap-1.5',
  md: 'min-h-[50px] rounded-[17px] px-4 py-3 text-body gap-2',
  lg: 'min-h-[56px] rounded-[20px] px-5 py-3.5 text-body gap-2',
};

export interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BtnVariant;
  size?: BtnSize;
  loading?: boolean;
}

export const Btn = forwardRef<HTMLButtonElement, BtnProps>(
  ({ variant = 'primary', size = 'md', loading, disabled, children, className, ...rest }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cls(
        'inline-flex items-center justify-center font-ui font-semibold transition-all duration-150 hover:-translate-y-px disabled:cursor-not-allowed disabled:hover:translate-y-0',
        SIZES[size],
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span
          aria-hidden
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      ) : (
        children
      )}
    </button>
  ),
);
Btn.displayName = 'Btn';
